import React, { useState, useEffect } from 'react';
import {
  Eye,
  EyeOff,
  Key,
  UserCheck,
  UserX,
  Copy,
  CheckCircle,
  XCircle,
  X
} from 'lucide-react';
import {
  createClientAuthAccount,
  disableClientAuth,
  resetClientPassword,
  checkClientAuthAccess,
  generateRandomPassword
} from '../utils/clientAuth';
import { sendAccessDetailsEmail } from '../services/clientAuthEmailService';
import { Mail } from 'lucide-react';

function ClientAuthManager({ db, client, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [authEmail, setAuthEmail] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isActivating, setIsActivating] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    loadAuthStatus();
  }, [client]);

  const loadAuthStatus = async () => {
    if (isActivating) return; // Ne pas écraser pendant l'activation
    try {
      const status = await checkClientAuthAccess(db, client.id);
      setHasAccess(status.hasAccess);

      // Mettre à jour l'email si disponible
      if (status.authEmail) {
        setAuthEmail(status.authEmail);
      } else if (client.authEmail) {
        setAuthEmail(client.authEmail);
      }

      // Toujours prioriser le mot de passe du document s'il existe
      // Sinon garder le mot de passe généré localement
      if (status.authPassword) {
        setGeneratedPassword(status.authPassword);
      } else if (client.authPassword && !generatedPassword) {
        setGeneratedPassword(client.authPassword);
      }
    } catch (error) {
      console.error('Erreur lors du chargement du statut auth:', error);
    }
  };

  const enableAuthAccess = async () => {
    setLoading(true);
    setIsActivating(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await createClientAuthAccount(
        db,
        client.id,
        client.email,
        '', // Le mot de passe sera généré par la Cloud Function
        client
      );

      if (result.success) {
        setHasAccess(true);
        setAuthEmail(result.email);
        setGeneratedPassword(result.password);
        setMessage({
          type: 'success',
          text: 'Accès bénéficiaire activé avec succès ! Veuillez noter les identifiants ci-dessous.'
        });

        // Rafraîchir le statut pour synchroniser avec Firestore
        await loadAuthStatus();
      } else {
        setMessage({
          type: 'error',
          text: `Erreur: ${result.error}`
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Erreur: ${error.message}`
      });
    } finally {
      setLoading(false);
      setIsActivating(false);
    }
  };

  const disableAuthAccess = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await disableClientAuth(db, client.id);

      if (result.success) {
        setHasAccess(false);
        setAuthEmail(null);
        setGeneratedPassword('');
        setMessage({
          type: 'success',
          text: 'Accès client désactivé'
        });
        onUpdate?.();
      } else {
        setMessage({
          type: 'error',
          text: `Erreur: ${result.error}`
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Erreur: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await resetClientPassword(db, client.id);

      if (result.success) {
        setGeneratedPassword(result.newPassword);
        setMessage({
          type: 'success',
          text: 'Mot de passe réinitialisé'
        });
        // Recharger le statut après réinitialisation
        await loadAuthStatus();
      } else {
        setMessage({
          type: 'error',
          text: `Erreur: ${result.error}`
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Erreur: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setMessage({
      type: 'success',
      text: 'Copié dans le presse-papier'
    });
    setTimeout(() => setMessage({ type: '', text: '' }), 2000);
  };

  const handleSendEmail = async () => {
    if (!authEmail && !client.authEmail) return;

    setSendingEmail(true);
    setMessage({ type: '', text: '' });

    try {
      const emailToSend = authEmail || client.authEmail;
      // On envoie le mot de passe seulement s'il est visible (nouveau ou reset)
      // Sinon on envoie sans mot de passe (juste l'accès)
      const pwdToSend = generatedPassword || client.authPassword;

      await sendAccessDetailsEmail(client, emailToSend, pwdToSend);

      setMessage({
        type: 'success',
        text: 'Email envoyé avec succès !'
      });
    } catch (error) {
      console.error("Erreur envoi email:", error);
      setMessage({
        type: 'error',
        text: "Erreur lors de l'envoi de l'email"
      });
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md max-w-md w-full mx-4">
      {/* En-tête avec bouton fermeture */}
      <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
        <h3 className="text-xl font-bold text-gray-900 flex items-center">
          <Key className="mr-3 text-blue-600" size={24} />
          Accès Client
        </h3>
        <button
          onClick={() => onUpdate?.()}
          className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
          title="Fermer"
        >
          <X size={20} />
        </button>
      </div>

      {/* Messages d'état */}
      {message.text && (
        <div className={`mb-6 p-4 rounded-lg border ${message.type === 'success'
          ? 'bg-green-50 text-green-800 border-green-200'
          : 'bg-red-50 text-red-800 border-red-200'
          }`}>
          <div className="flex items-center">
            {message.type === 'success' ? (
              <CheckCircle className="mr-2 flex-shrink-0" size={18} />
            ) : (
              <XCircle className="mr-2 flex-shrink-0" size={18} />
            )}
            <span className="text-sm font-medium">{message.text}</span>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Section Statut */}
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {hasAccess ? (
                <UserCheck className="text-green-600 mr-3" size={24} />
              ) : (
                <UserX className="text-gray-400 mr-3" size={24} />
              )}
              <div>
                <p className="font-semibold text-gray-900">
                  {hasAccess ? 'Accès activé' : 'Accès désactivé'}
                </p>
                <p className="text-sm text-gray-600">
                  {hasAccess ? 'Le client peut se connecter' : 'Aucun accès client'}
                </p>
              </div>
            </div>

            {hasAccess ? (
              <button
                onClick={disableAuthAccess}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors font-medium text-sm"
              >
                {loading ? '...' : 'Désactiver'}
              </button>
            ) : (
              <button
                onClick={enableAuthAccess}
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors font-medium text-sm"
              >
                {loading ? '...' : 'Activer'}
              </button>
            )}
          </div>
        </div>

        {/* Informations d'authentification */}
        {hasAccess && (authEmail || client.authEmail) && (
          <div className="space-y-4">
            <div className="border border-gray-200 rounded-lg p-4 bg-white">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                <Key className="mr-2 text-blue-600" size={18} />
                Identifiants de connexion
              </h4>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email de connexion
                  </label>
                  <div className="flex items-center">
                    <input
                      type="text"
                      value={authEmail || client.authEmail || ''}
                      readOnly
                      className="flex-1 p-3 border border-gray-300 rounded-l-lg bg-gray-50 text-sm"
                    />
                    <button
                      onClick={() => copyToClipboard(authEmail || client.authEmail)}
                      className="p-3 bg-blue-600 text-white rounded-r-lg hover:bg-blue-700 transition-colors"
                      title="Copier l'email"
                    >
                      <Copy size={16} />
                    </button>
                  </div>
                </div>

                {/* Bouton d'envoi d'email */}
                <button
                  onClick={handleSendEmail}
                  disabled={sendingEmail || (!authEmail && !client.authEmail)}
                  className="w-full flex items-center justify-center px-4 py-2 border border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors font-medium mb-2 disabled:opacity-50"
                  title="Envoyer les identifiants par email"
                >
                  <Mail className="mr-2" size={18} />
                  {sendingEmail ? 'Envoi en cours...' : 'Envoyer les identifiants par email'}
                </button>

                {(generatedPassword || client.authPassword) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mot de passe
                    </label>
                    <div className="flex items-center">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={generatedPassword || client.authPassword || ''}
                        readOnly
                        className="flex-1 p-3 border border-gray-300 rounded-l-lg bg-gray-50 font-mono text-sm"
                      />
                      <button
                        onClick={() => setShowPassword(!showPassword)}
                        className="p-3 bg-gray-200 text-gray-700 border-l border-gray-300 hover:bg-gray-300 transition-colors"
                        title={showPassword ? 'Masquer' : 'Afficher'}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                      <button
                        onClick={() => copyToClipboard(generatedPassword || client.authPassword)}
                        className="p-3 bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                        title="Copier le mot de passe"
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={resetPassword}
              disabled={loading}
              className="w-full px-4 py-3 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 transition-colors font-medium flex items-center justify-center"
            >
              <Key className="mr-2" size={18} />
              {loading ? 'Réinitialisation en cours...' : 'Réinitialiser le mot de passe'}
            </button>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h4 className="font-semibold text-blue-800 mb-3 flex items-center">
            📋 Instructions
          </h4>
          <ul className="text-sm text-blue-700 space-y-2">
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span><strong>URL de connexion:</strong> https://omadservices.agentime.fr/client-login</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Le client verra uniquement son propre planning</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Toutes les autres fonctionnalités sont masquées</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span><strong>Communiquez les identifiants au client en toute sécurité</strong></span>
            </li>
          </ul>
        </div>
      </div>
    </div >
  );
}

export default ClientAuthManager;
